import * as React from 'react';
import { FluentMessageBar } from '../FluentMessageBar/FluentMessageBar';
import { TeachingBubble } from '../TeachingBubble/TeachingBubble';
import styled from 'styled-components';
import { Button } from '@fluentui/react-components';
import { useEffect, useMemo, useState } from 'react';
import { cloneDeep } from '@apollo/client/utilities';
// @ts-ignore
import { Analytics } from 'mixpanel';

type Step = {
  target: string;
  primaryBtnText?: string;
  primaryBtnAction?: () => void;
  text: string;
  visible?: boolean;
};

type GuidedTourPropsType = {
  title?: string;
  message?: string;
  steps?: Step[];
  tourId: string;
  activeStep?: number | null;
  onTourStarted?: () => void;
};

const GuidedTourContainer = styled.div`
  padding-left: 16px;
  padding-right: 16px;
`;

export const getCompletedTourIds = () => {
  let completedTours = localStorage.getItem('completedTours');
  return completedTours
    ? completedTours
        .split(',')
        .map((tour) => tour.trim())
        .filter((tour) => tour !== '')
    : [];
};

export const markTourComplete = (tourId: string, tourSkipped: boolean) => {
  if (!tourSkipped) {
    Analytics.track('Guided tour completed');
  } else {
    Analytics.track('Guided tour banner dismissed');
  }

  let tourIds = getCompletedTourIds();
  if (!tourIds.includes(tourId)) {
    tourIds.push(tourId);
  }
  let updatedTours = tourIds.join(',');
  localStorage.setItem('completedTours', updatedTours);
};

export const GuidedTour = ({ title, message, steps, tourId, activeStep, onTourStarted }: GuidedTourPropsType) => {
  const [tourStart, setTourStart] = useState(false);
  const [tourSteps, setTourSteps] = useState(steps);
  const [currentStep, setCurrentStep] = useState(0);

  let tourIds = getCompletedTourIds();
  const [tourComplete, setTourComplete] = useState(tourIds?.includes(tourId));

  const handleLinkClick = useMemo(
    () => (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'A') {
        event.preventDefault();
        if (tourSteps && tourSteps.length > 0) {
          const updatedSteps = tourSteps.map((step) => ({
            ...step,
            visible: false,
          }));
          setTourSteps(updatedSteps);
          setCurrentStep(0);
        }
      }
    },
    [tourSteps]
  );

  useEffect(() => {
    document.addEventListener('click', handleLinkClick);
    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  useEffect(() => {
    const shallowClone = tourSteps?.map((step) => ({
      ...step,
    }));

    setTourSteps(shallowClone);
  }, [steps]);

  useEffect(() => {
    if (activeStep && activeStep > 0) {
      moveNextStep(activeStep);
    }
  }, [activeStep]);

  useEffect(() => {
    if (!tourComplete) {
      Analytics.track('Guided tour banner visible');
    }
  }, [tourComplete]);

  useEffect(() => {
    let tourIds = getCompletedTourIds();
    setTourComplete(tourIds?.includes(tourId));
  }, [tourId]);

  const moveNextStep = useMemo(
    () => (stepOverrideValue?: number) => {
      const tourStepsClone = cloneDeep(tourSteps);
      let step = stepOverrideValue ?? currentStep;

      if (tourStepsClone) {
        const currentStepClone = tourStepsClone[step - 1];
        const nextStepClone = tourStepsClone[step];

        if (currentStepClone) {
          currentStepClone.visible = false;
        }

        if (nextStepClone) {
          nextStepClone.visible = true;
        }

        setTourSteps(tourStepsClone);
        setCurrentStep(step + 1);
      }
    },
    [tourSteps, currentStep]
  );

  if (tourComplete) {
    return null;
  } else {
    return (
      <>
        {!tourStart && (
          <GuidedTourContainer>
            <FluentMessageBar
              title={title}
              message={message}
              visible={true}
              onClose={() => {
                markTourComplete(tourId, true);
              }}
              action={
                <Button
                  onClick={() => {
                    if (tourSteps && tourSteps.length > currentStep) {
                      Analytics.track('Guided tour learn more selected');
                      setTourStart(true);
                      const tourStepsClone = cloneDeep(tourSteps);
                      const firstStepClone = tourStepsClone[currentStep];
                      firstStepClone.visible = true;
                      setTourSteps(tourStepsClone);
                      setCurrentStep(currentStep + 1);
                      onTourStarted?.();
                    }
                  }}
                >
                  Learn More
                </Button>
              }
            />
          </GuidedTourContainer>
        )}
        {tourSteps?.map((step, index) => (
          <TeachingBubble
            key={`${step?.text}-${step?.target}`}
            visible={step.visible ?? false}
            primaryBtnText={step.primaryBtnText}
            hideSecondaryBtn={index === tourSteps?.length - 1}
            primaryBtnAction={() => {
              step?.primaryBtnAction?.();
              if (tourSteps && currentStep < tourSteps.length) {
                moveNextStep();
              } else {
                markTourComplete(tourId, false);
              }
            }}
            text={step.text}
            target={step.target}
            onDismiss={() => {
              Analytics.track('Guided tour abandoned');
            }}
          />
        ))}
      </>
    );
  }
};
